import { Button } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { notify, translate } from '../../../core'
import { BadRequestException } from '../../../core/type'
import { executeQuery } from '../../../core/query'
import { usePrinter } from '../../../core/component/Printer/printer.hook'
import { GetOrderMailingLabel } from '../graphql/order-queries.graphql'
import { OrderMailingLabel } from '../type'

export type OrderMailLabelButtonProps = {
  orderId?: number
  unsaved: boolean
}

export const OrderMailLabelButton = ({ orderId, unsaved }: OrderMailLabelButtonProps) => {
  const { print } = usePrinter()
  const [printing, setPrinting] = useState(false)

  const handlePrint = async () => {
    // Block re-entry so a double-click cannot send duplicate labels to the printer.
    if (!orderId || printing)
      return

    // The backend builds the label from the saved order, so edits still on the
    // form would not be on it.
    if (unsaved)
      return notify.error(translate('mail_label'), translate('mail_label_save_first_message'))

    setPrinting(true)
    try {
      const { orderMailingLabel }: { orderMailingLabel: OrderMailingLabel } = await executeQuery({
        documentNode: GetOrderMailingLabel,
        variables: { id: orderId },
      })

      await print(
        {
          template_name: orderMailingLabel.templateName,
          variables: orderMailingLabel.variables,
        },
        { keepSlash: true },
      )

      notify.success(translate('mail_label'), translate('mail_label_sent_message'))
    } catch (e) {
      notify.error(
        translate('mail_label'),
        e instanceof BadRequestException
          ? e.message
          : translate('print_failed_message'),
      )
    } finally {
      setPrinting(false)
    }
  }

  return (
    <Button
      onClick={handlePrint}
      disabled={!orderId}
      loading={printing}
      icon={<PrinterOutlined />}
    >
      {translate('mail_label')}
    </Button>
  )
}
